import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { MEMBER_ROLES, canRemoveMember, invitableRoles } from '@guabi/shared';
import { Button } from '@/components/ui/Button';
import { Drawer } from '@/components/ui/Drawer';
import { Field, SelectInput, TextInput } from '@/components/ui/Field';
import { Pill } from '@/components/ui/Pill';
import { ApiRequestError } from '@/lib/api';
import {
  useInviteMember,
  useMembers,
  useRemoveMember,
  useRevokeInvite,
  type InvitableRole,
  type InviteResult,
} from './queries';

/** Maps a server error to copy, by code — never by message. */
function messageFor(err: unknown, t: (k: string) => string): string {
  if (!(err instanceof ApiRequestError)) return t('common.somethingWrong');
  switch (err.code) {
    case 'conflict':
      return t('members.alreadyHere');
    case 'entitlement_required':
      return t('members.capReached');
    case 'forbidden':
      return t('members.notAllowed');
    case 'bad_request':
      return t('members.badEmail');
    default:
      return t('common.somethingWrong');
  }
}

export function MembersDrawer({
  wsId,
  open,
  onClose,
  myUserId,
}: {
  wsId: string;
  open: boolean;
  onClose: () => void;
  myUserId?: string;
}) {
  const { t } = useTranslation();
  const members = useMembers(open ? wsId : undefined);
  const invite = useInviteMember(wsId);
  const removeMember = useRemoveMember(wsId);
  const revokeInvite = useRevokeInvite(wsId);

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<InvitableRole>('partner');
  const [error, setError] = useState<string>();
  const [sent, setSent] = useState<InviteResult>();

  const callerRole = members.data?.callerRole;
  // The picker offers exactly what the server would accept from this role.
  const allowed = (callerRole ? invitableRoles(callerRole, MEMBER_ROLES) : []) as InvitableRole[];

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(undefined);
    setSent(undefined);
    try {
      const result = await invite.mutateAsync({ email, role, ...(name ? { name } : {}) });
      setSent(result);
      setEmail('');
      setName('');
    } catch (err) {
      setError(messageFor(err, t));
    }
  }

  return (
    <Drawer open={open} title={t('members.title')} onClose={onClose}>
      {members.isLoading && <p className="text-body text-leaf-soft">{t('common.loading')}</p>}
      {members.isError && <p className="text-body text-leaf-soft">{t('common.somethingWrong')}</p>}

      {members.data && (
        <div className="flex flex-col gap-6">
          <section className="flex flex-col gap-3">
            <h3 className="text-caption font-medium text-ink">{t('members.onThisWedding')}</h3>
            <ul className="flex flex-col gap-2">
              {members.data.members.map((m) => {
                const isSelf = m.userId === myUserId;
                return (
                  <li
                    key={m.userId}
                    className="flex flex-wrap items-center gap-2 border-b border-line pb-2"
                  >
                    <span className="text-body">{m.name || m.email || m.userId}</span>
                    <Pill tone={m.role === 'owner' ? 'booked' : 'neutral'}>
                      {t(`members.role.${m.role}`)}
                    </Pill>
                    {isSelf && (
                      <span className="text-caption text-leaf-soft">{t('members.you')}</span>
                    )}
                    {!m.acceptedAt && !isSelf && (
                      <span className="text-caption text-leaf-soft">
                        {t('members.notSignedIn')}
                      </span>
                    )}
                    {callerRole && canRemoveMember(callerRole, m.role, isSelf) && (
                      <button
                        type="button"
                        onClick={() => void removeMember.mutateAsync({ userId: m.userId })}
                        className="ml-auto min-h-tap text-caption text-status-declinedFg underline underline-offset-4"
                      >
                        {isSelf ? t('members.leave') : t('members.remove')}
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>

          {members.data.invites.length > 0 && (
            <section className="flex flex-col gap-3">
              <h3 className="text-caption font-medium text-ink">{t('members.pending')}</h3>
              <ul className="flex flex-col gap-2">
                {members.data.invites.map((i) => (
                  <li
                    key={i.email}
                    className="flex flex-wrap items-center gap-2 border-b border-line pb-2"
                  >
                    <span className="text-body">{i.email}</span>
                    <Pill tone="pending">{t(`members.role.${i.role}`)}</Pill>
                    <button
                      type="button"
                      onClick={() => void revokeInvite.mutateAsync({ email: i.email })}
                      className="ml-auto min-h-tap text-caption text-status-declinedFg underline underline-offset-4"
                    >
                      {t('members.withdraw')}
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {allowed.length > 0 ? (
            <form onSubmit={onSubmit} className="flex flex-col gap-4">
              <h3 className="text-caption font-medium text-ink">{t('members.inviteHeading')}</h3>

              <Field label={t('members.email')} hint={t('members.emailHint')}>
                {({ id, describedBy }) => (
                  <TextInput
                    id={id}
                    aria-describedby={describedBy}
                    type="email"
                    required
                    autoComplete="off"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                )}
              </Field>

              <Field label={t('members.name')}>
                {({ id }) => (
                  <TextInput
                    id={id}
                    maxLength={200}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                )}
              </Field>

              <Field label={t('members.roleLabel')}>
                {({ id }) => (
                  <SelectInput
                    id={id}
                    value={role}
                    onChange={(e) => setRole(e.target.value as InvitableRole)}
                  >
                    {allowed.map((r) => (
                      <option key={r} value={r}>
                        {t(`members.role.${r}`)}
                      </option>
                    ))}
                  </SelectInput>
                )}
              </Field>

              {error && (
                <p role="alert" className="text-body text-status-declinedFg">
                  {error}
                </p>
              )}
              {sent && (
                <p role="status" className="text-body text-leaf-soft">
                  {sent.provisioned === 'created'
                    ? t('members.sentNewAccount')
                    : t('members.sentExisting')}
                </p>
              )}

              <Button type="submit" loading={invite.isPending}>
                {t('members.send')}
              </Button>
            </form>
          ) : (
            <p className="text-body text-leaf-soft">{t('members.cannotInvite')}</p>
          )}
        </div>
      )}
    </Drawer>
  );
}
